import React, { useState } from "react";
import {
  Box,
  Typography,
  Stack,
  TextField,
  Button,
  Chip,
  Paper,
  useMediaQuery,
  Grid,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from "@mui/material";
import { styled, useTheme } from "@mui/material/styles";
import { Icons } from "../../assets/icon";
import { useSendViewerTip } from "../../../services/stagemode_api/viewers/viewers_api";
import { useAuth } from "../../../hooks";
import { useSnackbar } from "notistack";
import { motion } from "framer-motion";

const InteractionSection = styled(Paper)(() => ({
  padding: "24px",
  marginBottom: "24px",
  borderRadius: "16px",
  backgroundColor: "#1A1A1A",
  boxShadow: "0 4px 12px rgba(0,0,0,0.6)",
  fontFamily: "Lexend, sans-serif",
  color: "#fff",
}));

const ChipAmount = styled(Chip)<{ selected?: boolean }>(({ selected }) => ({
  fontFamily: "Lexend, sans-serif",
  padding: "8px 12px",
  fontSize: "14px",
  fontWeight: 500,
  borderRadius: "16px",
  backgroundColor: selected ? "#B91C1C" : "#2A2A2A",
  color: selected ? "#fff" : "#E5E7EB",
  border: selected ? "1px solid #DC2626" : "1px solid #444",
  "&:hover": {
    backgroundColor: "#991B1B",
    color: "#fff",
  },
  cursor: "pointer",
}));

const TipSection: React.FC<{ bookingId: string }> = ({ bookingId }) => {
  const [selectedChipAmount, setSelectedChipAmount] = useState<number | null>(
    null
  );
  const [customTipAmount, setCustomTipAmount] = useState<string>("");
  const [showThankYou, setShowThankYou] = useState(false);
  const [confirmAmount, setConfirmAmount] = useState<number | null>(null);
  const [openConfirmDialog, setOpenConfirmDialog] = useState(false);

  const { mutateAsync: sendTip } = useSendViewerTip(bookingId);
  const { userInfo } = useAuth();
  const { enqueueSnackbar } = useSnackbar();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const triggerThankYou = () => {
    setShowThankYou(true);
    setTimeout(() => setShowThankYou(false), 1500);
  };

  const handleChipTip = (amount: number) => {
    setConfirmAmount(amount);
    setOpenConfirmDialog(true);
  };

  const handleCustomTip = () => {
    const amount = parseInt(customTipAmount);
    if (!isNaN(amount) && amount > 0) {
      setConfirmAmount(amount);
      setOpenConfirmDialog(true);
    }
  };

  const confirmSendTip = async () => {
    if (confirmAmount) {
      setSelectedChipAmount(confirmAmount);
      setCustomTipAmount(confirmAmount.toString());
      await sendTip({ amount: confirmAmount });
      triggerThankYou();
    }
    setOpenConfirmDialog(false);
  };
  const purchasedChips = userInfo?.wallet_purchase?.available_chips || 0;
  const earnedChips = userInfo?.wallet_earning?.available_chips || 0;

  return (
    <InteractionSection>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <Icons.Coin height={20} width={20} />
        <Typography
          variant="h6"
          sx={{
            fontFamily: "Lexend",
            fontWeight: 600,
            fontSize: "18px",
            color: "#fff",
          }}
        >
          Send a Tip
        </Typography>
      </Box>

      <Typography
        variant="body2"
        sx={{ fontFamily: "Lexend", color: "#D1D5DB" }}
      >
        Support your favorite talent with quick chip tips or enter a custom
        amount.
      </Typography>

      <Typography
        variant="body2"
        sx={{
          fontFamily: "Lexend",
          color: "#10B981",
          mt: 1,
        }}
      >
        Available Balance: <strong>{purchasedChips + earnedChips} Chips</strong>
      </Typography>

      <Grid container spacing={1} mt={2} mb={3}>
        {[1, 2, 5, 10, 50, 100].map((amount) => (
          <Grid item xs={4} sm={2} key={amount}>
            <ChipAmount
              label={`${amount}`}
              icon={<Icons.Coin height={16} width={16} />}
              onClick={() => handleChipTip(amount)}
              selected={selectedChipAmount === amount}
            />
          </Grid>
        ))}
      </Grid>

      <Stack
        direction={isMobile ? "column" : "row"}
        spacing={2}
        alignItems={isMobile ? "stretch" : "center"}
        flexWrap="wrap"
      >
        <TextField
          size="small"
          label="Custom Tip"
          type="number"
          value={customTipAmount}
          onChange={(e) => {
            setCustomTipAmount(e.target.value);
            setSelectedChipAmount(null);
          }}
          placeholder="e.g. 25"
          InputProps={{
            startAdornment: (
              <Icons.Coin height={16} width={16} style={{ marginRight: 8 }} />
            ),
          }}
          sx={{
            width: isMobile ? "100%" : 180,
            "& .MuiOutlinedInput-root": {
              borderRadius: "16px",
              fontFamily: "Lexend",
              fontSize: "14px",
              backgroundColor: "#111",
              color: "#fff",
              border: "1px solid #333",
            },
            "& input": {
              color: "#fff",
            },
            "& label": {
              color: "#9CA3AF",
            },
          }}
        />

        <Button
          variant="contained"
          onClick={handleCustomTip}
          disabled={!customTipAmount || parseInt(customTipAmount) <= 0}
          sx={{
            width: isMobile ? "100%" : "auto",
            fontFamily: "Lexend",
            fontWeight: 600,
            fontSize: "14px",
            px: 3,
            py: 1.25,
            borderRadius: "16px",
            backgroundColor: "#E50914",
            textTransform: "none",
            "&:hover": {
              backgroundColor: "#B20710",
            },
            "&:disabled": {
              backgroundColor: "#333",
              color: "#888",
            },
          }}
        >
          Send Tip
        </Button>
      </Stack>

      {showThankYou && (
        <motion.div
          key="thankyou"
          initial={{ scale: 0.7, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.7, opacity: 0 }}
          transition={{ duration: 0.5 }}
        >
          <Typography
            variant="body2"
            sx={{
              fontFamily: "Lexend",
              color: "#10B981",
              mt: 3,
              textAlign: "center",
              fontWeight: 500,
            }}
          >
            🎉 Thank you for your tip!
          </Typography>
        </motion.div>
      )}

      {/* Confirmation Dialog */}
      <Dialog
        open={openConfirmDialog}
        onClose={() => setOpenConfirmDialog(false)}
        PaperProps={{
          sx: {
            backgroundColor: "#1A1A1A",
            borderRadius: "16px",
            padding: "24px",
            fontFamily: "Lexend, sans-serif",
            boxShadow: "0 0 20px rgba(229, 9, 20, 0.5)",
            color: "#fff",
            width: "100%",
            maxWidth: 400,
          },
        }}
      >
        <DialogTitle
          sx={{
            fontFamily: "Lexend, sans-serif",
            fontWeight: 600,
            fontSize: "20px",
            color: "#fff",
            paddingBottom: 0,
          }}
        >
          Confirm Your Tip
        </DialogTitle>

        <DialogContent
          sx={{
            fontFamily: "Lexend, sans-serif",
            fontSize: "16px",
            color: "#D1D5DB",
            marginTop: "12px",
          }}
        >
          Are you sure you want to send{" "}
          <strong style={{ color: "#F87171" }}>
            {confirmAmount} chip{confirmAmount && confirmAmount > 1 ? "s" : ""}
          </strong>
          ?
        </DialogContent>

        <DialogActions
          sx={{
            justifyContent: "flex-end",
            marginTop: 2,
          }}
        >
          <Button
            onClick={() => setOpenConfirmDialog(false)}
            sx={{
              fontFamily: "Lexend, sans-serif",
              color: "#9CA3AF",
              textTransform: "none",
              "&:hover": {
                color: "#fff",
              },
            }}
          >
            Cancel
          </Button>

          <Button
            onClick={confirmSendTip}
            variant="contained"
            sx={{
              backgroundColor: "#E50914",
              fontFamily: "Lexend, sans-serif",
              textTransform: "none",
              fontWeight: 600,
              paddingX: 3,
              paddingY: 1.25,
              borderRadius: "12px",
              "&:hover": {
                backgroundColor: "#B20710",
              },
            }}
          >
            Yes, Send
          </Button>
        </DialogActions>
      </Dialog>
    </InteractionSection>
  );
};
export default TipSection;
